import { getWalletColor, hexToRgba } from '@polaris/core/utils'
import { CirclePlus } from 'lucide-react'
import Image from 'next/image'
import { useTheme } from 'next-themes'
import type React from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/src/utils/styles'

interface WalletCardProps {
  wallet: {
    displayName: string
    logoUrl?: string
    id: string
    website?: string
  }
  isConnectable: boolean
  isConnected: boolean
  onConnect: () => void
  showAddAccountButton?: boolean
  showWalletBackground?: boolean
  subtitle?: ReactNode
  className?: string
}

export function WalletCard({
  wallet,
  isConnectable,
  isConnected,
  onConnect,
  showAddAccountButton = true,
  showWalletBackground = false,
  subtitle,
  className,
}: WalletCardProps) {
  const theme = useTheme()
  const isDarkMode = theme.theme === 'dark'

  const handleClick = () => {
    if (!isConnectable && wallet.website) {
      window.open(wallet.website, '_blank')
    } else {
      onConnect()
    }
  }

  const walletBg = showWalletBackground
    ? getWalletColor(wallet.id, 'BrowserExtension')
    : 'transparent'

  return (
    <button
      onClick={handleClick}
      className={cn(
        'z-10 aspect-[42/23] rounded-2xl overflow-hidden bg-background bg-gradient-to-br from-10% from-surface3-100 via-50% via-foreground to-70% to-surface2-100 flex transition-shadow text-left shadow-lg hover:shadow-xl',
        className,
      )}
      style={
        {
          '--wallet-bg': showWalletBackground
            ? hexToRgba(walletBg, 0.6)
            : isDarkMode
              ? 'rgba(58, 66, 58, 0.6)'
              : 'rgba(255, 255, 255, 0.6)',
        } as React.CSSProperties
      }
    >
      <div
        className='p-3 pb-2 flex flex-col gap-2 w-full justify-between rounded-2xl'
        style={{
          backgroundColor: 'var(--wallet-bg)',
        }}
      >
        <div className='flex items-start justify-between w-full'>
          {wallet.logoUrl && (
            <Image
              className='size-6 overflow-hidden rounded-md'
              src={wallet.logoUrl}
              alt={`${wallet.displayName} logo`}
              width={24}
              height={24}
              unoptimized
            />
          )}
          {isConnected && showAddAccountButton && (
            <CirclePlus className='size-6 text-tertiary' strokeWidth={1.5} />
          )}
        </div>
        <div className='flex flex-col text-sm tracking-tight text-left'>
          <div className='text-primary font-semibold text-ellipsis overflow-hidden whitespace-nowrap'>
            {wallet.displayName}
          </div>
          <div className='text-link h-5'>
            {subtitle ||
              (!isConnectable
                ? 'Install'
                : isConnected
                  ? 'Add account'
                  : 'Connect')}
          </div>
        </div>
      </div>
    </button>
  )
}
